import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUIStore } from "@/stores/ui";
import { LanguageSelect } from "@/components/ui/language-select";

export default function PrivacyPolicy() {
  const { t, i18n } = useTranslation();
  const { setLanguage } = useUIStore();

  const languages = [
    { code: "pt", name: "pt", flag: "🇧🇷" },
    { code: "en", name: "en", flag: "🇺🇸" },
    { code: "es", name: "es", flag: "🇪🇸" },
  ];

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setLanguage(langCode);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  return (
    <div className="h-screen overflow-y-auto bg-background text-foreground px-4 py-10">
      <div className="max-w-4xl mx-auto space-y-6 text-sm leading-relaxed">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-3xl font-bold">
            {t("privacy.title") || "Privacy Policy"}
          </h1>

          <LanguageSelect
            languages={languages}
            currentLanguage={i18n.language}
            onLanguageChange={handleLanguageChange}
            collapsed={false}
          />
        </div>

        <p>
          {t("privacy.intro") ||
            "This Privacy Policy applies to the Geoescala application (hereinafter referred to as the “Application”), developed and maintained by Geoescala (hereinafter referred to as the “Service Provider”). The Application is provided as a free service and is intended for use “AS IS”."}
        </p>

        <h2 className="text-xl font-semibold">
          {t("privacy.informationCollection") || "Information Collection and Use"}
        </h2>

        <p>
          {t("privacy.informationCollectionText") ||
            "The Application collects only the information strictly necessary for its operation and for providing its core functionality related to employee time and attendance management."}
        </p>

        <ul className="list-disc list-inside space-y-1">
          <li>
            {t("privacy.deviceInfo") ||
              "Basic device and technical information"}
          </li>
          <li>
            <strong>{t("privacy.location") || "Location data"}</strong>{" "}
            {t("privacy.locationDesc") ||
              "collected only at the moment of time registration"}
          </li>
          <li>
            <strong>{t("privacy.face") || "Facial images"}</strong>{" "}
            {t("privacy.faceDesc") ||
              "captured for identity verification purposes"}
          </li>
        </ul>

        <h2 className="text-xl font-semibold">
          {t("privacy.locationTitle") || "Location Information"}
        </h2>

        <ul className="list-disc list-inside space-y-1">
          <li>
            {t("privacy.locationOnce") ||
              "Location is collected only at the moment of registration"}
          </li>
          <li>
            {t("privacy.locationNotTracked") ||
              "Location is not tracked continuously"}
          </li>
          <li>
            {t("privacy.locationNoShare") ||
              "Location data is not shared with third parties"}
          </li>
          <li>
            {t("privacy.locationNoMarketing") ||
              "Location data is not used for marketing or analytics"}
          </li>
        </ul>

        <h2 className="text-xl font-semibold">
          {t("privacy.faceTitle") || "Facial Recognition and Camera Use"}
        </h2>

        <p>
          {t("privacy.faceText") ||
            "The Application may access the device camera solely for identity verification and fraud prevention in time and attendance records."}
        </p>

        <ul className="list-disc list-inside space-y-1">
          <li>
            {t("privacy.faceNoShare") ||
              "Facial images are not shared with third parties"}
          </li>
          <li>
            {t("privacy.faceNoMarketing") ||
              "Facial images are not used for marketing or advertising"}
          </li>
          <li>
            {t("privacy.faceNoPublic") ||
              "Facial images are not made public"}
          </li>
        </ul>

        <h2 className="text-xl font-semibold">
          {t("privacy.dataSharing") || "Data Sharing"}
        </h2>

        <ul className="list-disc list-inside space-y-1">
          <li>{t("privacy.noSell") || "Does not sell personal data"}</li>
          <li>
            {t("privacy.noShare") || "Does not share personal data"}
          </li>
          <li>
            {t("privacy.noAds") ||
              "Does not use personal data for advertising"}
          </li>
        </ul>

        <p>
          {t("privacy.legalDisclosure") ||
            "Personal data may be disclosed only if required by law."}
        </p>

        <h2 className="text-xl font-semibold">
          {t("privacy.retention") || "Data Retention Policy"}
        </h2>

        <p>
          {t("privacy.retentionText") ||
            "User data is retained only for as long as necessary to fulfill the purposes described in this Privacy Policy or as required by law."}
        </p>

        <p>
          {t("privacy.contactDelete") ||
            "Requests for data deletion can be sent to:"}{" "}
          <strong>suporte@geoescala.br</strong>
        </p>

        <h2 className="text-xl font-semibold">
          {t("privacy.security") || "Security"}
        </h2>

        <p>
          {t("privacy.securityText") ||
            "The Service Provider adopts reasonable technical and organizational measures to protect user data against unauthorized access, loss, or misuse."}
        </p>

        <h2 className="text-xl font-semibold">
          {t("privacy.changes") || "Changes to This Policy"}
        </h2>

        <p>
          {t("privacy.changesText") ||
            "This Privacy Policy may be updated from time to time. Continued use of the Application after changes means acceptance of the updated policy."}
        </p>

        <p className="italic">
          <strong>
            {t("privacy.effectiveDate") || "Effective date:"}
          </strong>{" "}
          January 19, 2026
        </p>

        <h2 className="text-xl font-semibold">
          {t("privacy.consent") || "Your Consent"}
        </h2>

        <p>
          {t("privacy.consentText") ||
            "By using the Application, you agree to the collection and use of information as described in this Privacy Policy."}
        </p>

        <h2 className="text-xl font-semibold">
          {t("privacy.contact") || "Contact Us"}
        </h2>

        <p>
          {t("privacy.contactText") ||
            "If you have any questions about this Privacy Policy, please contact:"}
        </p>

        <p>
          <strong>suporte@geoescala.br</strong>
        </p>
      </div>
    </div>
  );
}
